<template>
  <div class="members-section">
    <h3>Members</h3>
    <div class="invite-form" v-if="!props.readOnly && canInviteUsers(props.organizationId)">
      <input
        v-model="inviteFormData.email"
        type="email"
        placeholder="Email address"
        class="form-control"
      />
      <select
        v-model="inviteFormData.role"
        class="form-control"
      >
        <option value="">Select Role</option>
        <option v-for="role in sortedRoles" :key="role.id" :value="role.name">
          {{ role.name }}
        </option>
      </select>
      <button
        @click="handleInvite"
        class="submit-button medium"
        :disabled="!inviteFormData.email || !inviteFormData.role || inviteMutation.isPending.value"
      >
        <font-awesome-icon v-if="inviteMutation.isPending.value" icon="spinner" spin />
        <span v-else>Invite</span>
      </button>
    </div>

    <div class="success-message" v-if="success">
      {{ success }}
    </div>
    <div class="error-message" v-if="error">
      {{ error }}
    </div>

    <div v-if="groupedMembers.length + detailedInvites.length > 0" class="members-list">
      <div 
        v-for="member in groupedMembers" 
        :key="member.user_id" 
        :class="canClickMember ? 'member-row clickable' : 'member-row'"
        @click="canClickMember ? navigateToMember(member) : null"
      >
        <div class="member-info">
          <div class="member-name">
            {{ member.user_first_name }} {{ member.user_last_name }}
          </div>
          <div class="member-email">
            {{ member.user_email }}
          </div>
        </div>
        <RoleBadges :roles="member.roles.map(role => role.name)" :maxBadges=8 />
      </div>

      <div 
        v-for="invite in detailedInvites" 
        :key="invite.email" 
        class="invite-row"
      >
        <div class="invite-info">
          <div v-if="invite.expired" class="invite-name">
            Expired Invite
          </div>
          <div v-else class="invite-name">
            Invited Member
          </div>
          <div class="invite-email">
            {{ invite.email }}
          </div>
          <div v-if="invite.expired" class="invite-expiration">
            Invitation expired since {{ formatDate(invite.expires_at) }}
          </div>
          <div v-else class="invite-expiration">
            Invitation expires on {{ formatDate(invite.expires_at) }}
          </div>
        </div>
        <RoleBadges :roles="[invite.role_name]" />
        <button 
          v-if="!props.readOnly && canInviteUsers(props.organizationId)"
          class="action-button delete"
          @click="handleDeleteInvite(invite.id)"
          :disabled="deleteInviteMutation.isPending.value && deleteInviteMutation.variables.value === invite.id"
        >
          <font-awesome-icon v-if="deleteInviteMutation.isPending.value && deleteInviteMutation.variables.value === invite.id" icon="spinner" spin />
          <font-awesome-icon v-else icon="trash" />
        </button>
      </div>
    </div>
    <div v-else>
      No members found.
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import RoleBadges from '@/components/RoleBadges.vue';
import { useRouter } from 'vue-router';
import { isSuperuser } from '@/scripts/permissions';
import { handleApiError, showConfirmDialog, formatDate } from '@/scripts/shared-utils';
import { canInviteUsers, canEditUsers, isProtectedRole, sortRoles } from '@/scripts/permissions';
import { getOrganizationMembers, getOrganizationInvites, createOrganizationInvite, deleteOrganizationInvite, organizationKeys } from '@/scripts/api/organizationApi';
import { getRoles, roleKeys } from '@/scripts/api/rolesApi';

const router = useRouter();
const props = defineProps({
  organizationId: {
    type: String,
    required: true
  },
  readOnly: {
    type: Boolean,
    default: false
  }
});

const queryClient = useQueryClient();
const success = ref('');
const error = ref('');

const membersQueryKey = computed(() => organizationKeys.getOrganizationMembers(props.organizationId));

const { data: members } = useQuery({
  queryKey: membersQueryKey,
  queryFn: () => getOrganizationMembers(props.organizationId),
  staleTime: 60000,
  placeholderData: [],
  onError: (error) => handleApiError(error, 'fetching organization members')
});

const { data: roles } = useQuery({
  queryKey: roleKeys.getRoles(),
  queryFn: getRoles,
  staleTime: 60000,
  placeholderData: [],
  select: (data) => data.filter(role => !isProtectedRole(role.name)),
  onError: (error) => handleApiError(error, 'fetching organization roles')
});

const invitesQueryKey = computed(() => organizationKeys.getOrganizationInvites(props.organizationId));

const { data: invites } = useQuery({
  queryKey: invitesQueryKey,
  queryFn: () => getOrganizationInvites(props.organizationId),
  staleTime: 60000,
  placeholderData: [],
  onError: (error) => handleApiError(error, 'fetching organization invites')
});

const getDefaultInviteFormData = () => ({
  email: '',
  role: ''
});

const inviteFormData = ref(getDefaultInviteFormData());

const sortedRoles = computed(() => {
  return sortRoles(roles.value);
});

// Group members by user_id and combine their roles
const groupedMembers = computed(() => {
  const grouped = {};
  members.value.forEach(member => {
    if (!grouped[member.user_id]) {
      grouped[member.user_id] = {
        user_id: member.user_id,
        roles: [],
        organization_id: member.organization_id,
        user_first_name: member.user_first_name,
        user_last_name: member.user_last_name,
        user_email: member.user_email
      };
    }
    if (member.organization_role_id && member.role_name) {
      grouped[member.user_id].roles.push({
        "id": member.organization_role_id,
        "name": member.role_name,
      });
    }
  });
  return Object.values(grouped);
});

const detailedInvites = computed(() => {
  return invites.value.map(invite => ({
    ...invite,
    expired: new Date(invite.expires_at) < new Date()
  })).sort((a, b) => new Date(b.expires_at) - new Date(a.expires_at));
});

const canClickMember = computed(() => {
  return isSuperuser() || (!props.readOnly && canEditUsers(props.organizationId));
});

const navigateToMember = (member) => {
  // the click on the member is meant to be for editing its role,
  // but superuser in readonly mode can go to the user details page
  if (props.readOnly && isSuperuser()) {
    router.push(`/users/${member.user_id}`);
  } else {
    router.push(`/organizations/${props.organizationId}/members/${member.user_id}`)
  }
};

const inviteMutation = useMutation({
  mutationFn: (inviteData) => createOrganizationInvite(props.organizationId, inviteData),
  onSuccess: (data, variables) => {
    if (data.user) {
      success.value = `Successfully added ${data.user.first_name} ${data.user.last_name} as ${variables.role}`;
    } else {
      success.value = `Successfully sent invite email to ${variables.email}`;
    }
    error.value = '';
    inviteFormData.value = getDefaultInviteFormData();
    queryClient.invalidateQueries({ queryKey: membersQueryKey.value });
    queryClient.invalidateQueries({ queryKey: invitesQueryKey.value });
  },
  onError: (err) => {
    handleApiError(err, 'inviting member');
    success.value = '';
    error.value = err.message;
  },
});

const handleInvite = () => {
  inviteMutation.mutate(inviteFormData.value);
};

const deleteInviteMutation = useMutation({
  mutationFn: (inviteId) => deleteOrganizationInvite(props.organizationId, inviteId),
  onSuccess: () => {
    success.value = 'Successfully rescinded invite.';
    error.value = '';
    queryClient.invalidateQueries({ queryKey: invitesQueryKey.value });
  },
  onError: (err) => {
    handleApiError(err, 'deleting invite');
    success.value = '';
    error.value = err.message;
  },
});

const handleDeleteInvite = async (inviteId) => {
  if (await showConfirmDialog('Are you sure you want to rescind this invite?')) {
    deleteInviteMutation.mutate(inviteId);
  }
};
</script>

<style scoped>
@import '@/styles/shared-buttons.css';
@import '@/styles/organization-members.css';
</style>
